import React, { Component } from 'react';
import { inject } from 'mobx-react';
import { debounce } from 'lodash';
import Box from 'core/components/Box';
import Button from 'core/components/Button';
import Card from 'core/components/Card';
import Flex from 'core/components/Flex';
import Text from 'core/components/Text';
import Icon from 'core/components/Icon';
import Select from 'core/form/components/select/Select';
import Field from 'core/form/components/Field';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import Spinner from 'core/components/Spinner';
import { formConsumer } from 'core/form';
import {
  NOTIFICATION_TEST_AND_PREVIEW_TYPES,
  OAUTH2_FIELD_IDENTIFIERS
} from '@kentik/ui-shared/notifications/constants';
import InputGroup from 'core/form/components/InputGroup';
import { isValidUUID } from 'app/views/alerting/util/uuid';

const normalizeForComparison = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return value;
};

@formConsumer
@inject('$notifications')
export default class NotificationPreview extends Component {
  state = {
    isAlertType: true,
    previewLoading: false,
    previewContent: undefined,
    previewError: undefined
  };

  componentDidMount() {
    this.getPreviewContent();
  }

  getPreviewContent = () => {
    const { $notifications, form, model } = this.props;
    const notificationType = form.getValue('notificationType');
    const trimmedAlertModelId = form.getValue('alertModelId')?.trim();

    if (!form.valid) {
      return;
    }

    this.setState({ previewLoading: true });

    const baseChannel = model.serialize({ ...form.getValues(), channelID: model.get('channelID') });
    const previewPayload = { channel: { ...baseChannel } };

    if (trimmedAlertModelId) {
      previewPayload.channel.alertModelId = trimmedAlertModelId;
      previewPayload.notification_event = { alertmanEvent: { alarmID: trimmedAlertModelId } };
    } else {
      previewPayload.test_model = { model_type: notificationType };
    }

    $notifications
      .previewChannel(previewPayload)
      .then(({ content, error }) => {
        this.setState({
          previewLoading: false,
          previewContent: content,
          previewError: error
        });
      })
      .catch((previewError) => {
        this.setState({
          previewLoading: false,
          previewError: previewError?.message || 'Failed to load preview'
        });
      });
  };

  handleNotificationTypeChange = (typeField) => {
    this.setState(
      {
        isAlertType:
          typeField.value === NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_ALARM_MODEL ||
          typeField.value === NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_NMS_ALARM_MODEL ||
          typeField.value === NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_SYNTH_ALARM_MODEL,
        previewError: undefined,
        previewContent: undefined
      },
      this.getPreviewContent
    );
  };

  handleAlertModelChange = debounce(() => {
    const { form } = this.props;
    const alertModelId = form.getValue('alertModelId');

    if (alertModelId && !isValidUUID(alertModelId.trim())) {
      return;
    }

    this.getPreviewContent();
  }, 500);

  hasOauthConfigChanged = () => {
    const { form, model } = this.props;

    return OAUTH2_FIELD_IDENTIFIERS.some((fieldPath) => {
      const currentValue = form.getValue(fieldPath);
      const originalValue = model.get(fieldPath);

      return normalizeForComparison(currentValue) !== normalizeForComparison(originalValue);
    });
  };

  render() {
    const { $notifications, role, form, notificationTestLoading, handleTestClick } = this.props;
    const notificationType = form.getValue('notificationType');
    const channelType = form.getValue('channelType');

    const oauth2AccessTokenURL = form.getValue('config.sendingConfig.oauth2AccessTokenURL');
    const oauth2ClientId = form.getValue('config.sendingConfig.oauth2ClientId');
    const oauth2ClientSecret = form.getValue('config.sendingConfig.oauth2ClientSecret');

    const { isAlertType, previewContent, previewError, previewLoading } = this.state;
    const ariaProps = {};

    if (role) {
      ariaProps.role = role;
    }

    const oauthSupportedForChannel = $notifications.channelTypeSupportOAuth(channelType);
    const oauthConfiguredForChannel =
      oauthSupportedForChannel &&
      !!oauth2AccessTokenURL &&
      (typeof oauth2AccessTokenURL === 'string' ? oauth2AccessTokenURL.trim() !== '' : true) &&
      (!!oauth2ClientId || !!oauth2ClientSecret);

    const oauthConfigChanged = oauthConfiguredForChannel && this.hasOauthConfigChanged();

    return (
      <Box mx={1} {...ariaProps} maxWidth="95vw">
        {oauthConfigChanged && (
          <Flex mb={2} alignItems="center">
            <Icon icon={BsFillExclamationTriangleFill} color="warning" mr={1} />
            <Text color="warning" fontSize={12}>
              This channel is configured to use OAuth 2.0 and you have unsaved changes to its OAuth configuration. To be
              able to accurately test or preview notifications for this channel, please save your changes first so that
              credentials can be created/updated and associated with the channel.
            </Text>
          </Flex>
        )}
        <Flex justifyContent="space-between">
          <Field
            m={0}
            name="notificationType"
            onChange={this.handleNotificationTypeChange}
            options={$notifications.supportedNotificationTypesForPreviewOptions}
          >
            <Select values={notificationType} width="150px" menuWidth="150px" />
          </Field>
          {isAlertType ? (
            <Field m={0} name="alertModelId" onChange={this.handleAlertModelChange} minWidth="300px">
              <InputGroup />
            </Field>
          ) : null}
        </Flex>
        <Card mt={2} p={1}>
          <Flex flexDirection="column" height={oauthConfigChanged ? 530 : 560} overflow="hidden">
            <Box flex={1} overflow="auto">
              {previewLoading ? (
                <Flex justifyContent="center" alignItems="center" height="100%">
                  <Spinner size={24} />
                </Flex>
              ) : (
                <pre style={{ whiteSpace: 'pre-wrap' }}>{previewContent}</pre>
              )}
            </Box>
            {previewError && (
              <div>
                <Icon icon={BsFillExclamationTriangleFill} color="warning" mr={1} />
                <Text color="warning" fontSize={12}>
                  {previewError}
                </Text>
              </div>
            )}
            {!form.valid && (
              <div>
                <Icon icon={BsFillExclamationTriangleFill} color="danger" mr={1} />
                <Text color="danger" fontSize={12}>
                  Model preview is unavailable for invalid notification channels. Please check your settings.
                </Text>
              </div>
            )}
          </Flex>
        </Card>

        <Flex gap={1} alignItems="center" mt={3}>
          <Button
            disabled={!form.valid}
            text="Send Test Notification"
            loading={notificationTestLoading}
            onClick={handleTestClick}
          />
        </Flex>
      </Box>
    );
  }
}
